/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
// import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

// components imports
import { Avatar, Button, Card, Col, Form, Row, Skeleton, Space } from "antd";
import { EditOutlined, FieldTimeOutlined } from "@ant-design/icons";
import { BankOutlined, MailOutlined, UserOutlined } from "@ant-design/icons";
import { Container } from "@/components/UI/Container";

// misc imports
import { useUserInfo } from "@/api/user";
import replaceUrlParams from "@/utils/string/replaceUrlParams";
import { NAV_EDIT_PROFILE } from "@/constants";
import { LanguageSelector } from "@/components/UI/LanguageSelector";
import { tokens as Tokens } from "@/utils/tokens";
import { queryClient } from "@/utils/queryClient";
import { ITokens } from "@/interfaces/User.interface";

const baseClassName = "profile-screen";

/**
 * Component to handle display the user information - my profile
 * @returns JSX.Element
 */
export const ProfileScreen = (): JSX.Element => {
  const { t } = useTranslation();
  const tokensInstance = Tokens;
  const userAuth:ITokens | undefined = queryClient.getQueryData(["user-auth"]);
  const { access: accessToken } = userAuth || {};
  const { data, isLoading } = useUserInfo(tokensInstance.getTokenSub(accessToken) as string);
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  return (
    <Container>
      <Row justify={"center"}>
        <Col xs={24} md={16}>
          <Card title={t("menu.profile")}>
            {isLoading ? (
              <Skeleton />
            ) : (
              // User Information
              <>
                <Space align="center" className="mb-4" size={12}>
                  <Avatar size={64} icon={<UserOutlined />} />
                  <h3 className="mb-0">
                    {data?.first_name} {data?.last_name}
                  </h3>
                </Space>
                <h5 className={`${baseClassName}__info mb-5`}>
                  <MailOutlined className="mr-1" />
                  &nbsp;{data?.email}
                  <br />
                  <BankOutlined className="mr-1" />
                  &nbsp;{data?.company}
                  <br />
                  <FieldTimeOutlined className="mr-1" />
                  &nbsp;{data?.timezone || tz}
                </h5>
                {/* Form to handle the Language selection */}
                <Form>
                  <Form.Item label={t("common.change_language")}>
                    <LanguageSelector />
                  </Form.Item>
                </Form>
                {/* ./ Form to handle the Language selection */}

                {/* Edit Profile Button */}
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() =>
                    navigate(
                      replaceUrlParams(NAV_EDIT_PROFILE, {
                        workspaceId: workspaceId as string,
                      })
                    )
                  }
                >
                  {t("common.edit")}
                </Button>
                {/* ./ Edit Profile Button */}
              </>
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
};
